import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

function AthenaStar() {
  const navigate = useNavigate();
  const [hasRefAccess, setHasRefAccess] = useState(false);

  // ── Mirror Navbar refAccess logic ─────────────────────────────────────────
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const userData = localStorage.getItem('userData');
        const user = userData ? JSON.parse(userData) : null;
        if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
          setHasRefAccess(true);
        } else {
          setHasRefAccess(localStorage.getItem('refAccess') === '1');
        }
      } catch {
        setHasRefAccess(false);
      }
    };

    checkRefAccess();

    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res = await fetch(
          `${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };

    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) {
              localStorage.setItem('refAccess', value);
              setHasRefAccess(value === '1');
            }
          }
        } catch {}
      }
      checkRefAccess();
    };

    const pollInterval = setInterval(pollRefAccess, 10000);
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('refAccessChange', checkRefAccess);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('refAccessChange', checkRefAccess);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Spacer to prevent content hiding behind fixed/sticky navbar */}
      <div className="h-16 sm:h-20 w-full"></div>

      {/* Protection styles */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* ─── HERO SECTION ─────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 pt-20 pb-14 sm:pt-28 sm:pb-20 min-h-[60vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="/Assests/home_page_134.png"
            alt="Athena Star Hero Background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/55"></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">

          {/* Main Heading */}
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight max-w-4xl">
            Athena Star: Revolutionizing Learning Through Movies, Videos, Books, and Podcasts
          </h1>

          {/* Subheading */}
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-400 mb-4 sm:mb-6 leading-tight max-w-5xl">
            Where Education Meets Entertainment
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-base lg:text-lg leading-relaxed max-w-3xl mb-8">
            An immersive OTT platform that transforms how students discover, engage, and grow. Say goodbye to passive studying and hello to dynamic, multimedia-rich experiences that captivate and inspire.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <button
            //   onClick={() => navigate('/reffrence?productId=5')}
              className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition w-full sm:w-auto">
              Start Learning Today
            </button>
            <button
            //   onClick={() => navigate('/reffrence?productId=5')}
              className="px-8 py-3 border border-white text-white rounded-md font-medium hover:bg-white hover:text-orange-500 hover:border-orange-500 transition w-full sm:w-auto">
              Explore the Platform
            </button>
          </div>

        </div>
      </section>

      {/* ─── DISCOVER A NEW WAY TO LEARN ──────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-14 sm:py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="/Assests/home_page_135.png"
            alt="Discover a New Way to Learn Background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            ✦ THE FUTURE OF EDUCATION
          </div>

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Discover a New Way to Learn
          </h2>

          {/* First Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-6 max-w-5xl">
            Transform your educational journey with Athena Star's immersive OTT platform. Say goodbye to passive studying and hello to dynamic, multimedia-rich experiences that captivate and inspire.
          </p>

          {/* Second Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-5xl">
            Engage with rich content designed to make learning exciting and deeply effective — from cinematic documentaries to bite-sized video lessons, interactive eBooks, and expert-hosted podcasts. Every format is crafted to meet learners where they are.
          </p>

        </div>
      </section>

      {/* ─── WHY CHOOSE ATHENA STAR ───────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto">

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Why Choose Athena Star?
          </h2>
          <p className="text-gray-400 text-lg mb-12">
            One platform. Four powerful formats. Limitless possibilities for every kind of learner.
          </p>

          {/* Three Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Card 1 - Personalized Experience */}
            <div className="border-2 border-orange-500 rounded-2xl overflow-hidden bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="w-full h-48 overflow-hidden">
                <img
                  src="/Assests/home_page_136.png"
                  alt="Personalized Experience"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
              <h3 className="text-xl font-bold text-white mb-4">Personalized Experience</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Every learner is unique. Athena Star tailors content to each student's pace, preferences, and learning style for maximum impact.
              </p>
              </div>
            </div>

            {/* Card 2 - All-in-One Platform */}
            <div className="border-2 border-orange-500 rounded-2xl overflow-hidden bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="w-full h-48 overflow-hidden">
                <img
                  src="/Assests/home_page_137.png"
                  alt="All-in-One Platform"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
              <h3 className="text-xl font-bold text-white mb-4">All-in-One Platform</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Access movies, videos, books, and podcasts from a single destination — no switching apps, no distractions, just seamless learning.
              </p>
              </div>
            </div>

            {/* Card 3 - Learn Anywhere */}
            <div className="border-2 border-orange-500 rounded-2xl overflow-hidden bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="w-full h-48 overflow-hidden">
                <img
                  src="/Assests/home_page_138.png"
                  alt="Learn Anywhere"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
              <h3 className="text-xl font-bold text-white mb-4">Learn Anywhere</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Mobile-optimized and always available. Whether at home, in class, or on the go — your education travels with you.
              </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── THE POWER OF STORYTELLING ────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* 3-Column Layout: Image | Text | Why Stories Work */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-start">

            {/* Column 1 - Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[480px]">
              <img
                src="/Assests/home_page_139.png"
                alt="The Power of Storytelling"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Column 2 - Badge + Heading + Paragraphs */}
            <div className="space-y-5">
              {/* Badge */}
              {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                📖 LEARNING SCIENCE
              </div> */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                The Power of Storytelling in Education
              </h2>
              <p className="text-gray-300 text-base leading-relaxed">
                Research shows that learning through stories enhances memory retention by up to <strong className="text-white">65%</strong> compared to rote memorization. Narratives create emotional connections that anchor knowledge in long-term memory.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                Athena Star curates content that bridges abstract concepts with real-world narratives — making subjects like physics, history, and literature come alive through compelling storytelling.
              </p>
            </div>

            {/* Column 3 - Why Stories Work Card */}
            <div className="bg-orange-600 rounded-2xl p-7">
              <h3 className="text-lg font-bold text-white mb-5">Why Stories Work</h3>
              <div className="space-y-4">
                {[
                  'Activate multiple brain regions simultaneously',
                  'Create emotional anchors for complex concepts',
                  'Build contextual understanding naturally',
                  'Improve recall and critical thinking',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-white text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── MOVIES THAT TEACH ────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Badge */}
          {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🎬 MOVIES
          </div> */}

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Movies That Teach: Learning Beyond Textbooks
          </h2>

          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-12 max-w-5xl">
            Athena Star's curated film library brings education to the big screen. From science explorations to historical dramas, each film is selected for its ability to illuminate complex topics through cinematic storytelling.
          </p>

          {/* Four Category Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* Science */}
            <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="mb-4">
                <svg className="w-9 h-9 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.798-1.338 2.798a1.875 1.875 0 01-1.324-.549l-1.942-1.942" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-orange-400 mb-3">Science</h3>
              <p className="text-gray-300 text-sm leading-relaxed">Documentaries exploring the cosmos, biology, and innovation</p>
            </div>

            {/* History */}
            <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="mb-4">
                <svg className="w-9 h-9 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-orange-400 mb-3">History</h3>
              <p className="text-gray-300 text-sm leading-relaxed">Interactive films bringing pivotal moments to vivid life</p>
            </div>

            {/* Literature */}
            <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="mb-4">
                <svg className="w-9 h-9 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-orange-400 mb-3">Literature</h3>
              <p className="text-gray-300 text-sm leading-relaxed">Adaptations that deepen understanding of classic works</p>
            </div>

            {/* Geography */}
            <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50 hover:bg-black/70 transition-all duration-300">
              <div className="mb-4">
                <svg className="w-9 h-9 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-orange-400 mb-3">Geography</h3>
              <p className="text-gray-300 text-sm leading-relaxed">Visual journeys across cultures, landscapes, and ecosystems</p>
            </div>

          </div>
        </div>
      </section>

      {/* ─── ENGAGING VIDEO LESSONS ───────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/80">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div key={i} className="absolute bg-orange-200 rounded-full opacity-20"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Main Heading - centered like screenshot */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-4 leading-tight text-center">
            Engaging Video Lessons for Every Subject
          </h2>
          <p className="text-black text-lg mb-10 text-center max-w-4xl mx-auto">
            Short, focused, and visually dynamic — Athena Star's video lessons are designed to clarify complex topics in minutes, not hours.
          </p>

          {/* Three Horizontal Cards — image left, text right */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">

            {/* Card 1 - Visual Clarity */}
            <div className="bg-black/20 border border-orange-400/40 rounded-xl p-4 flex flex-row items-start gap-4">
              {/* Small square image */}
              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                <img
                  src="/Assests/home_page_140.png"
                  alt="Visual Clarity"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Text */}
              <div>
                <h3 className="text-base font-bold text-black mb-2">Visual Clarity</h3>
                <p className="text-black/80 text-sm leading-relaxed">
                  Rich animations and graphics break down abstract concepts into digestible, visual explanations that stick.
                </p>
              </div>
            </div>

            {/* Card 2 - Multi-Sensory Learning */}
            <div className="bg-black/20 border border-orange-400/40 rounded-xl p-4 flex flex-row items-start gap-4">
              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                <img
                  src="/Assests/home_page_141.png"
                  alt="Multi-Sensory Learning"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-base font-bold text-black mb-2">Multi-Sensory Learning</h3>
                <p className="text-black/80 text-sm leading-relaxed">
                  Catering to both visual and auditory learners, each video integrates narration, on-screen text, and illustrative graphics.
                </p>
              </div>
            </div>

            {/* Card 3 - Every Subject Covered */}
            <div className="bg-black/20 border border-orange-400/40 rounded-xl p-4 flex flex-row items-start gap-4">
              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                <img
                  src="/Assests/home_page_142.png"
                  alt="Every Subject Covered"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-base font-bold text-black mb-2">Every Subject Covered</h3>
                <p className="text-black/80 text-sm leading-relaxed">
                  From algebra to zoology — our growing video library spans the full curriculum with expert-produced content.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── DIGITAL LIBRARY ──────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* 3-Column Layout: Text | Feature Cards | Tall Image */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-start">

            {/* Column 1 - Badge + Heading + Paragraphs */}
            <div className="space-y-5">
              {/* Badge */}
              {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                📖 DIGITAL LIBRARY
              </div> */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                Books That Inspire and Inform
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed">
                Athena Star's curated digital library puts thousands of interactive eBooks and audiobooks at your fingertips. Each title is selected by educators for its ability to inform, challenge, and inspire learners of all ages.
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                Go beyond passive reading with built-in tools that transform every book into an active learning experience.
              </p>
            </div>

            {/* Column 2 - Three Feature Cards stacked */}
            <div className="space-y-4">
              {[
                { title: 'Smart Annotations', desc: 'Highlight, bookmark, and take notes directly in-text' },
                { title: 'Embedded Quizzes', desc: 'Test comprehension after every chapter' },
                { title: 'Read-Along Mode', desc: 'Synchronized audio narration for immersive reading' },
              ].map((item, idx) => (
                <div key={idx} className="border-2 border-orange-500 rounded-xl p-5 bg-black/50">
                  <h3 className="text-base font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Column 3 - Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[480px]">
              <img
                src="/Assests/home_page_143.png"
                alt="Digital Library"
                className="w-full h-full object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ─── PODCASTS ─────────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/75"></div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Badge */}
          {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🎙️ PODCASTS
          </div> */}

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Podcasts: Learning on the Go
          </h2>

          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-12 max-w-5xl">
            Education shouldn't stop when you leave your desk. Athena Star's expert-led podcast library covers trending educational topics — from scientific breakthroughs to creative writing workshops.
          </p>

          {/* Three Feature Columns */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">

            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                ),
                title: 'Expert Hosts',
                desc: 'Learn from leading educators, researchers, and industry professionals across every subject.',
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
                title: 'Flexible Listening',
                desc: 'Perfect for commutes, workouts, and downtime — turn any moment into a learning opportunity.',
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                ),
                title: 'Trending Topics',
                desc: 'Stay current with episodes on AI, climate science, literature, and more — updated weekly.',
              },
            ].map((item, idx) => (
              <div key={idx} className="space-y-4">
                <div className="w-14 h-14 border-2 border-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {item.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-orange-400">{item.title}</h3>
                <p className="text-gray-300 text-base leading-relaxed">{item.desc}</p>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ─── PERSONALIZED LEARNING PATHS ──────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/80 overflow-hidden">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div key={i} className="absolute bg-orange-200 rounded-full opacity-20"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">

          {/* Heading + Subtext */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-3 leading-tight">
            Personalized Learning Paths
          </h2>
          <p className="text-black text-base mb-10">
            No two learners are the same — and neither are their journeys on Athena Star.
          </p>

          {/* 3-Column: Left Boxes | Center SVG Infinity | Right Boxes */}
          <div className="grid grid-cols-3 gap-3 sm:gap-6 items-center">

            {/* Column 1 - Left boxes */}
            <div className="flex flex-col gap-8">
              <div className="border-2 border-black/40 rounded-xl px-2 sm:px-5 py-3 sm:py-5 text-center">
                <p className="text-black font-bold text-xs sm:text-sm leading-tight">Assess Skills &<br/>Interests</p>
              </div>
              <div className="border-2 border-black/40 rounded-xl px-2 sm:px-5 py-3 sm:py-5 text-center">
                <p className="text-black font-bold text-xs sm:text-sm leading-tight">Track Progress<br/>& Adapt</p>
              </div>
            </div>

            {/* Column 2 - Center: 2 overlapping circles with arrow */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 220 160" className="w-full max-w-[220px]" fill="none">
                {/* Left circle - darker orange */}
                <circle cx="85" cy="80" r="65" fill="#c2410c" opacity="0.9"/>
                {/* Right circle - lighter orange */}
                <circle cx="135" cy="80" r="65" fill="#ea580c" opacity="0.75"/>
                {/* Center arrow pointing right */}
                <polygon points="100,72 120,80 100,88" fill="#7c2d12"/>
              </svg>
            </div>

            {/* Column 3 - Right boxes */}
            <div className="flex flex-col gap-8">
              <div className="border-2 border-black/40 rounded-xl px-2 sm:px-5 py-3 sm:py-5 text-center">
                <p className="text-black font-bold text-xs sm:text-sm leading-tight">AI Recommends<br/>Content</p>
              </div>
              <div className="border-2 border-black/40 rounded-xl px-2 sm:px-5 py-3 sm:py-5 text-center">
                <p className="text-black font-bold text-xs sm:text-sm leading-tight">Engage with<br/>Media</p>
              </div>
            </div>

          </div>

          {/* Bottom description */}
          <p className="text-black text-sm mt-10 max-w-3xl">
            Our AI-driven recommendation engine continuously adapts content to individual progress and interests, ensuring learners stay motivated and challenged at precisely the right level — never bored, never overwhelmed.
          </p>

        </div>
      </section>

      {/* ─── INTERACTIVE FEATURES ─────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Left Side - Badge + Heading + Text + 3 Feature Rows */}
            <div className="space-y-6">

              {/* Badge */}
              {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                ⚡ INTERACTIVE LEARNING
              </div> */}

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                Interactive Features That Boost Engagement
              </h2>

              <p className="text-gray-300 text-base leading-relaxed">
                Learning isn't a spectator sport. Athena Star integrates powerful interactive tools directly into every piece of content, encouraging active participation and deeper comprehension.
              </p>

              {/* 3 Feature Rows with orange chevron icon box */}
              <div className="space-y-0 border border-orange-500/50 rounded-xl overflow-hidden">

                {/* Row 1 - Quizzes */}
                <div className="flex items-center gap-4 p-5 border-b border-orange-500/30">
                  {/* Chevron icon box */}
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                    <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none">
                      <polygon points="4,4 44,4 44,32 24,44 4,32" fill="none" stroke="#ea580c" strokeWidth="2"/>
                      <polyline points="15,22 22,29 33,18" stroke="#ea580c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-0.5">Quizzes</h3>
                    <p className="text-gray-400 text-sm">Test understanding in real-time after every lesson</p>
                  </div>
                </div>

                {/* Row 2 - Discussion Forums */}
                <div className="flex items-center gap-4 p-5 border-b border-orange-500/30">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                    <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none">
                      <polygon points="4,4 44,4 44,32 24,44 4,32" fill="none" stroke="#ea580c" strokeWidth="2"/>
                      <path d="M16 18h16M16 24h10" stroke="#ea580c" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-0.5">Discussion Forums</h3>
                    <p className="text-gray-400 text-sm">Collaborate and debate with peers across the globe</p>
                  </div>
                </div>

                {/* Row 3 - Live Q&A */}
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                    <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none">
                      <polygon points="4,4 44,4 44,32 24,44 4,32" fill="none" stroke="#ea580c" strokeWidth="2"/>
                      <rect x="14" y="16" width="20" height="14" rx="2" stroke="#ea580c" strokeWidth="2"/>
                      <path d="M34 21l8-4v14l-8-4" stroke="#ea580c" strokeWidth="2" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-0.5">Live Q&A</h3>
                    <p className="text-gray-400 text-sm">Ask experts questions during scheduled live sessions</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Side - Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[480px]">
              <img
                src="/Assests/home_page_144.png"
                alt="Interactive Learning"
                className="w-full h-full object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ─── SUPPORTING EDUCATORS AND PARENTS ────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Heading + subtext */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
            Supporting Educators and Parents
          </h2>
          <p className="text-gray-300 text-base mb-10 max-w-3xl">
            Athena Star empowers the people who guide learning — giving teachers and parents the visibility and tools they need to make a real difference.
          </p>

          {/* Two Cards — image top, title + text bottom */}
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Card 1 - Progress Tracking */}
            <div className="space-y-4">
              <div className="w-full h-52 rounded-xl overflow-hidden">
                <img
                  src="/Assests/home_page_145.png"
                  alt="Progress Tracking"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold text-white">Progress Tracking</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Detailed dashboards let educators and parents monitor student engagement, quiz scores, and learning milestones in real time.
              </p>
            </div>

            {/* Card 2 - Customizable Lesson Plans */}
            <div className="space-y-4">
              <div className="w-full h-52 rounded-xl overflow-hidden">
                <img
                  src="/Assests/home_page_146.png"
                  alt="Customizable Lesson Plans"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold text-white">Customizable Lesson Plans</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Teachers can curate playlists of movies, videos, books, and podcasts aligned to their curriculum — creating seamless blended learning experiences.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ─── ACCESSIBILITY AND INCLUSIVITY ────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/75"></div>

        <div className="max-w-7xl mx-auto relative z-10">

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

            {/* Left Side - Text */}
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Accessibility and Inclusivity at the Core
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                Education is a right, not a privilege. Athena Star is built from the ground up to serve diverse learning needs, abilities, and languages — ensuring no learner is left behind.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our mobile-friendly platform works seamlessly on any device, making quality education accessible anytime, anywhere — from urban classrooms to rural communities.
              </p>
            </div>

            {/* Right Side - Features List */}
            <div className="space-y-4">
              {[
                'Multi-Language Support',
                'Subtitles & Closed Captions',
                'Adjustable Playback Speed',
                'Offline Download Mode',
                'Screen Reader Compatible',
              ].map((feat, idx) => (
                <div key={idx} className="flex items-center gap-4 border-b border-gray-700 pb-4">
                  <div className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0"></div>
                  <p className="text-white text-lg font-semibold">{feat}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ─── SUCCESS STORIES ──────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/80">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
              style={{
                width: Math.random() * 3 + 1 + 'px',
                height: Math.random() * 3 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                animationDelay: Math.random() * 2 + 's',
                animationDuration: Math.random() * 3 + 2 + 's'
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Badge */}
          {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-100 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            ☆ SUCCESS STORIES
          </div> */}

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Real Success Stories
          </h2>
          <p className="text-black text-lg mb-12 max-w-3xl">
            Athena Star is already transforming learning outcomes for students and educators worldwide.
          </p>

          {/* Three Testimonials */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">

            {[
              {
                quote: 'My grades jumped from B\'s to A\'s after just three months. The documentary films made history feel real — not just dates on a page.',
                name: 'Priya S.',
                role: 'Grade 10 Student',
              },
              {
                quote: 'Athena Star transformed my classroom. Students are more engaged, ask better questions, and actually look forward to assignments.',
                name: 'Mr. Rajesh K.',
                role: 'Science Teacher',
              },
              {
                quote: 'The podcasts helped my daughter develop a love for reading. She now explores topics on her own — that\'s every parent\'s dream.',
                name: 'Meera T.',
                role: 'Parent',
              },
            ].map((t, idx) => (
              <div key={idx} className="bg-orange-600 rounded-2xl p-8 space-y-6 relative">
                <p className="text-white text-base italic leading-relaxed">"{t.quote}"</p>
                <div>
                  <p className="text-white font-bold">— {t.name}</p>
                  <p className="text-orange-200 text-sm">{t.role}</p>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ─── EXPERT CONTENT CREATORS ──────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Column 1 - Left Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[520px]">
              <img
                src="/Assests/home_page_147.png"
                alt="Expert Content Creators"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Column 2 - Right: Badge + Heading + Text + 4 Feature Rows */}
            <div className="space-y-5">

              {/* Badge */}
              {/* <div className="inline-flex items-center gap-2 bg-orange-700/70 text-orange-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                👥 OUR TEAM
              </div> */}

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                Behind the Scenes: Our Expert Content Creators
              </h2>

              <p className="text-gray-300 text-sm leading-relaxed">
                Every piece of content on Athena Star is born from collaboration between world-class educators, award-winning filmmakers, published authors, and seasoned podcasters.
              </p>

              {/* 4 Feature Rows — orange bordered */}
              <div className="space-y-3">
                {[
                  { icon: '', title: 'Filmmakers', desc: 'Cinematic quality that captivates and educates' },
                  { icon: '', title: 'Educators', desc: 'Curriculum-aligned content reviewed for accuracy' },
                  { icon: '', title: 'Authors', desc: 'Compelling narratives that deepen understanding' },
                  { icon: '', title: 'Podcasters', desc: 'Engaging audio that makes complex topics approachable' },
                ].map((item, idx) => (
                  <div key={idx} className="border border-orange-500/60 rounded-2xl px-5 py-4 bg-black/40 flex items-center gap-4">
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <h3 className="text-sm font-bold text-white mb-0.5">{item.title}</h3>
                      <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTINUOUS INNOVATION ────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Column 1 - Left: Heading + Text + 4 Numbered Items */}
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                Continuous Innovation and Updates
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed max-w-md">
                Education never stands still — and neither does Athena Star. Our platform evolves constantly to keep learners ahead of the curve.
              </p>

              {/* 4 Numbered Items — orange circle + horizontal line + title + desc */}
              <div className="space-y-6">
                {[
                  { num: '1', title: 'Weekly Content Drops', desc: 'Fresh movies, videos, books, and podcast episodes added every week' },
                  { num: '2', title: 'AI-Powered Features', desc: 'Smarter recommendations, adaptive quizzes, and intelligent search' },
                  { num: '3', title: 'Emerging Tech Integration', desc: 'AR experiences, interactive simulations, and gamified learning modules' },
                  { num: '4', title: 'Community Feedback Loop', desc: 'User suggestions directly shape our product roadmap and content priorities' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    {/* Orange circle number */}
                    <div className="flex items-center gap-3 flex-shrink-0 mt-0.5">
                      <div className="w-8 h-8 border-2 border-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {item.num}
                      </div>
                      {/* Horizontal line */}
                      <div className="w-6 h-px bg-orange-500"></div>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Column 2 - Right Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[520px]">
              <img
                src="/Assests/home_page_148.png"
                alt="Continuous Innovation"
                className="w-full h-full object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ─── SAFE AND SECURE ──────────────────────────────────────────────── */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ backgroundImage: "url('/Assests/home_page_58.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="max-w-4xl mx-auto relative z-10 px-4 sm:px-8">

          {/* Center dark card */}
          <div className="bg-black/80 rounded-2xl border border-gray-600 overflow-hidden">

            {/* Card heading bar */}
            <div className="px-8 pt-8 pb-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                Safe and Secure Learning Environment
              </h2>
            </div>

            {/* Card body: 2 columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">

              {/* Left - Our Promise orange box */}
              <div className="bg-orange-500 m-4 rounded-xl p-7 flex flex-col justify-center min-h-[220px]">
                <h3 className="text-base font-bold text-black mb-3">Our Promise</h3>
                <p className="text-black text-sm leading-relaxed">
                  Every learner deserves a space to explore, question, and grow — free from worry. Athena Star is built on trust.
                </p>
              </div>

              {/* Right - 4 numbered security items */}
              <div className="px-6 py-4 space-y-0">
                {[
                  { num: '01', title: 'GDPR & COPPA Compliant', desc: 'Meets the highest global data protection standards' },
                  { num: '02', title: 'End-to-End Encryption', desc: 'All personal data is encrypted in transit and at rest' },
                  { num: '03', title: 'Parental Controls', desc: 'Full visibility and control over content and screen time' },
                  { num: '04', title: 'Ad-Free Experience', desc: 'Zero ads — learning without distractions or tracking' },
                ].map((item, idx) => (
                  <div key={idx} className="border-b border-gray-700 py-3">
                    <p className="text-gray-400 text-xs font-semibold mb-0.5">{item.num}</p>
                    <h4 className="text-sm font-bold text-white mb-0.5">{item.title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      

      {/* ─── HOW TO GET STARTED ───────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Left: Badge + Heading + Text + 3 Numbered Steps */}
            <div className="space-y-6">
              {/* Badge */}
              {/* <div className="inline-flex items-center gap-2 bg-orange-700/70 text-orange-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                🚀 GET STARTED
              </div> */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                How to Get Started with Athena Star
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed">
                Begin your learning revolution in just three simple steps — no complicated setup, no commitments.
              </p>

              {/* 3 Numbered Steps */}
              <div className="space-y-6">
                {[
                  { num: '1', title: 'Sign Up in Seconds', desc: 'Create your free account at athenastar.in with just an email address. Start exploring immediately with our generous free trial.' },
                  { num: '2', title: 'Choose Your Plan', desc: 'Flexible subscription options for individuals, families, schools, and institutions — designed to fit every budget.' },
                  { num: '3', title: 'Start Learning', desc: 'Dive into thousands of movies, videos, books, and podcasts. Your personalized learning journey begins instantly.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 border-2 border-orange-500 rounded-full flex items-center justify-center text-orange-400 font-bold text-sm flex-shrink-0 mt-0.5">
                      {item.num}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[460px]">
              <img
                src="/Assests/home_page_149.png"
                alt="Get Started with Athena Star"
                className="w-full h-full object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ─── JOIN A GROWING COMMUNITY ─────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full opacity-40"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Left: Heading + Text + 3 Icon Features */}
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                Join a Growing Community of Learners
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed">
                Learning is better together. Athena Star connects you with a vibrant global community of curious minds.
              </p>

              {/* 3 Icon Features */}
              <div className="space-y-6">
                {[
                  {
                    icon: (
                      <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ),
                    title: 'Collaborative Projects',
                    desc: 'Team up with peers across the world on creative and academic projects that build real skills.',
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    ),
                    title: 'Challenges & Competitions',
                    desc: 'Participate in weekly learning challenges that keep motivation high and reward curiosity.',
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    ),
                    title: 'Earn Certifications',
                    desc: 'Complete learning paths and earn verifiable certificates that showcase your achievements.',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5">{item.icon}</div>
                    <div>
                      <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Tall Image */}
            <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[520px]">
              <img
                src="/Assests/home_page_150.png"
                alt="Growing Community of Learners"
                className="w-full h-full object-cover"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-900/60">
        <div className="max-w-5xl mx-auto">

          {/* Badge */}
          {/* <div className="inline-flex items-center gap-2 bg-orange-800/60 text-orange-300 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            ❓ FAQ
          </div> */}

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {[
              {
                q: 'What devices are supported?',
                a: 'Athena Star works on smartphones, tablets, laptops, and desktops — iOS, Android, and web browsers. Download the app or use any modern browser.',
              },
              {
                q: 'Is there a free trial?',
                a: 'Yes! Sign up and explore our full content library free for 14 days. No credit card required to start.',
              },
              {
                q: 'Do you offer school or institutional plans?',
                a: 'Absolutely. We offer bulk licensing, admin dashboards, and custom curriculum integration for schools and institutions of all sizes.',
              },
              {
                q: 'Can I access content offline?',
                a: 'Yes — download movies, videos, books, and podcasts for offline learning. Perfect for areas with limited connectivity.',
              },
              {
                q: 'How do I get help?',
                a: 'Our support team is available 24/7 via chat, email, and our comprehensive help center at athenastar.in.',
              },
            ].map((item, idx) => (
              <div key={idx} className="border-2 border-orange-500/50 rounded-2xl p-6 bg-black/40">
                <h3 className="text-lg font-bold text-white mb-2">{item.q}</h3>
                <p className="text-gray-300 text-base leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────────────────── */}
      <section className="relative px-6 sm:px-10 lg:px-24 py-20 bg-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Learning Experience?
          </h2>
          <p className="text-gray-400 text-lg mb-4 max-w-3xl">
            The future of education is here — and it's engaging, accessible, and built for you. Explore Athena Star today and discover a world where learning feels like an adventure.
          </p>
          <p className="text-gray-400 text-lg mb-12 max-w-3xl">
            Visit{" "}
            <a href="https://athenastar.in" target="_blank" rel="noopener noreferrer" className="text-orange-500 font-semibold hover:underline">
              athenastar.in
            </a>{" "}
            to access movies, videos, books, and podcasts designed for success.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
              <h3 className="text-2xl font-bold text-white mb-4">For Students</h3>
              <p className="text-gray-300 leading-relaxed">Immersive, personalized content across movies, videos, books, and podcasts to accelerate your learning journey.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
              <h3 className="text-2xl font-bold text-white mb-4">For Schools & Institutions</h3>
              <p className="text-gray-300 leading-relaxed">Bulk licensing, admin dashboards, and custom curriculum integration for institutions of all sizes.</p>
            </div>
          </div>
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4">
            {hasRefAccess && (
              <button
                onClick={() => navigate('/reffrence?productId=5')}
                className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Reference
              </button>
            )}
            <button
            onClick={() => window.location.href = "https://athenastar.in"}
            className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition w-full sm:w-auto">
            Get Started Free
            </button>

            <button
            onClick={() => window.location.href = "https://athenastar.in"}
            className="px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white hover:border-orange-500 hover:text-orange-500 transition w-full sm:w-auto">
            Explore Content Library
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}

export default AthenaStar;